import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [recaptchaConfig, setRecaptchaConfig] = useState<{ siteKey: string; enabled: boolean } | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Load reCAPTCHA configuration on component mount
  useEffect(() => {
    const loadRecaptchaConfig = async () => {
      try {
        const response = await fetch('http://localhost:5000/api/auth/recaptcha-config');
        const data = await response.json();
        if (data.success) {
          setRecaptchaConfig(data.data);
        }
      } catch (error) {
        console.error('Failed to load reCAPTCHA config:', error);
      }
    };
    
    loadRecaptchaConfig();
  }, []);

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Check reCAPTCHA if enabled
    if (recaptchaConfig?.enabled && !recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    try {
      await login(email, password, recaptchaToken);
      navigate('/admin');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
      // Reset reCAPTCHA on failed login
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setForgotLoading(true);

    try {
      const response = await authAPI.forgotPassword(forgotEmail);
      if (response.success) {
        setSuccessMessage('Password reset link has been sent to your email address.');
        setShowForgotPassword(false);
        setForgotEmail('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>🏛️ Sukoharjo Directory</h1>
          <h2>Admin Login</h2>
        </div>

        {!showForgotPassword ? (
          <form onSubmit={handleSubmit} className="login-form">
            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}
            
            {successMessage && (
              <div className="success-message">
                ✅ {successMessage}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="dpmptsp.skh@gmail.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password:</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Enter your password"
              />
            </div>

            {/* reCAPTCHA */}
            {recaptchaConfig?.enabled && recaptchaConfig.siteKey && (
              <div className="form-group recaptcha-group">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={recaptchaConfig.siteKey}
                  onChange={handleRecaptchaChange}
                  theme="light"
                />
              </div>
            )}

            <button
              type="submit"
              className="login-button"
              disabled={loading || !email || !password || (recaptchaConfig?.enabled && !recaptchaToken)}
            >
              {loading ? '🔄 Signing in...' : '🔐 Sign In'}
            </button>

            <div className="login-footer">
              <button 
                type="button" 
                className="reset-link"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                  setSuccessMessage('');
                }}
              >
                Forgot your password?
              </button>
            </div>
          </form>
        ) : (
          /* Forgot Password Form */
          <form onSubmit={handleForgotPassword} className="login-form">
            <div className="forgot-header">
              <h3>🔑 Reset Password</h3>
              <p>Enter your email address and we'll send you a reset link.</p>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}
            
            {successMessage && (
              <div className="success-message">
                ✅ {successMessage}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="forgot-email">Email Address:</label>
              <input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                disabled={forgotLoading}
                placeholder="dpmptsp.skh@gmail.com"
              />
            </div>

            <div className="forgot-actions">
              <button
                type="button"
                className="back-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotEmail('');
                  setError('');
                  setSuccessMessage('');
                }}
                disabled={forgotLoading}
              >
                ← Back to Login
              </button>
              
              <button
                type="submit"
                className="reset-button"
                disabled={forgotLoading || !forgotEmail}
              >
                {forgotLoading ? '📧 Sending...' : '📧 Send Reset Link'}
              </button>
            </div>
          </form>
        )}

        <div className="default-credentials">
          <p><strong>🔑 Default Credentials:</strong></p>
          <p>📧 Email: dpmptsp.skh@gmail.com</p>
          <p>🔒 Password: A_Sastro_378</p>
        </div>
        
        <div className="security-notice">
          <p>🔒 This login is protected by reCAPTCHA and secured with industry-standard encryption.</p>
        </div>
      </div>
    </div>
  );
};
